import { Button, Callout, Modal } from '@dev-spendesk/grapes';
import React, { useEffect, useMemo } from 'react';

import { useTranslation } from 'common/hooks/useTranslation';
import { NotificationType, useNotifications } from 'modules/app/notifications';
import { formatMonetaryValue } from 'src/core/utils/monetaryValue';

import {
  useCancelTransferErrorMessage,
  useCancelTransfers,
  useConfirmTransfersTrackingEvents,
} from '../../hooks';
import { type Transfer } from '../../models';

type Props = {
  isOpen: boolean;
  transfers: Transfer[];
  onClose: () => void;
  onConfirm?: () => void;
};

export const TransferCancelModal = ({
  isOpen,
  transfers,
  onClose,
  onConfirm,
}: Props) => {
  const { t } = useTranslation('global');
  const confirmTransfersTrackingEvents = useConfirmTransfersTrackingEvents();
  const { pushNotif } = useNotifications();
  const [
    cancelTransfers,
    cancelTransfersQueryState,
    resetCancelTransfersQueryState,
  ] = useCancelTransfers();

  const getCancelTransferErrorMessage = useCancelTransferErrorMessage();

  useEffect(() => {
    if (isOpen) {
      resetCancelTransfersQueryState();
    }
  }, [isOpen]);

  const handleCancelTransfers = async () => {
    confirmTransfersTrackingEvents.onConfirmTransfersRejectionModalConfirmed({
      transfers,
    });
    const response = await cancelTransfers({ transferIds });
    pushNotif({
      type: NotificationType.Success,
      message: t(
        response.outcome === 'cancellation_initiated'
          ? 'invoices.transfer.cancel.initiationSuccess'
          : 'invoices.transfer.cancel.success',
        {
          count: transfersCount,
        },
      ),
    });
    onClose();

    if (onConfirm) {
      onConfirm();
    }
  };

  const transferIds = useMemo(
    () => transfers.map((transfer) => transfer.id),
    [transfers],
  );

  const transfersCount = transfers.length;

  if (transfersCount === 0) {
    return null;
  }

  // Display the amount of the first transfer only, otherwise it will
  // not show an amount e.g. "Cancel 2 transfers"
  const firstTransferAmount = transfers[0].amount;

  return (
    <Modal
      actions={[
        <Button
          key="cancel"
          onClick={onClose}
          text={t('invoices.transfer.modal.reject.cancel')}
          variant="secondaryNeutral"
          isDisabled={cancelTransfersQueryState.status === 'loading'}
        />,
        <Button
          key="reject-transfers"
          text={t('invoices.transfer.modal.reject.confirm', {
            count: transfersCount,
          })}
          variant="primaryWarning"
          onClick={handleCancelTransfers}
        />,
      ]}
      iconName="triangle-warning"
      iconVariant="warning"
      title={t('invoices.transfer.modal.reject.title', {
        amount: formatMonetaryValue(firstTransferAmount),
        count: transfersCount,
      })}
      isOpen={isOpen}
    >
      <p>
        {t('invoices.transfer.modal.reject.subtitle', {
          count: transfersCount,
        })}
      </p>
      {cancelTransfersQueryState.status === 'error' && (
        <Callout
          title={getCancelTransferErrorMessage(
            cancelTransfersQueryState.error,
            { count: transfersCount },
          )}
          variant="alert"
          className="mt-8"
        />
      )}
    </Modal>
  );
};
